// The machine's usage drawn and said the same on its page (views/Machine.tsx) and in its card (Machine.tsx): the
// sparkline and the figures. The disk's meter is PixelMeter.

import type { KeyboardEvent, PointerEvent } from 'react';
import type { Usage, UsageSample } from '../api';

/** The machine card's span: its last two minutes, as the agent samples it (every 5 s). */
export const CARD_SPAN = 120;

/** The samples within `seconds` of the newest, by the machine's own clock. */
export function recent(u: Usage, seconds: number): UsageSample[] {
  const s = u.samples ?? [];
  const end = s.at(-1)?.t;
  return end === undefined ? [] : s.filter((x) => x.t > end - seconds);
}

const GiB = 2 ** 30;
/** GiB, one decimal under 10, whole above: "5.4 GB". */
export const gb = (n: number) => `${(n / GiB).toFixed(n / GiB < 10 ? 1 : 0)} GB`;
/** What a figure is of: "of 32 GB", whole GiB. */
export const ofGb = (n: number) => `of ${Math.round(n / GiB)} GB`;
export const pct = (v: number) => `${v < 10 ? v.toFixed(1) : Math.round(v)}%`;

const W = 240;
/** Where a sample sits on a chart h tall, from the top: the well's top quarter stays empty (14px of the page's 64). */
const yOf = (v: number, lo: number, hi: number, h: number) => h - 2 - ((v - lo) / (hi - lo)) * (h - Math.round(h / 4));

/**
 * A sparkline scaled to its own range, like a terminal graph: the shape matters, the value is printed above.
 * alt: a second series (out, write) in ink under the accent one, on the same scale. at: the sample being read,
 * marked by a hairline and a dot on each line.
 */
export function Spark({
  vals,
  alt,
  at = null,
  onAt,
  label,
  h = 64,
}: {
  vals: number[];
  alt?: number[];
  at?: number | null;
  /** Reading it (the usage card); without it the chart is only looked at (the machine card's, 28 tall). */
  onAt?: (i: number | null) => void;
  label: string;
  h?: number;
}) {
  const H = h;
  const style = h === 64 ? undefined : { height: h };
  if (vals.length < 2) return <div className="ws-spark-wrap" style={style} data-still={onAt ? undefined : ''} aria-hidden="true" />;
  const all = alt ? [...vals, ...alt] : vals;
  const lo = Math.min(...all);
  const hi = Math.max(...all) > lo ? Math.max(...all) : lo + 1;
  const last = vals.length - 1;
  const path = (vs: number[]) => 'M' + vs.map((v, i) => `${((i * W) / (vs.length - 1)).toFixed(1)} ${yOf(v, lo, hi, H).toFixed(1)}`).join('L');
  const line = path(vals);
  const svg = (
    <svg className="ws-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} aria-hidden="true">
      <path d={`${line}L${W} ${H}L0 ${H}Z`} className="ws-spark-area" />
      {alt && alt.length > 1 && <path d={path(alt)} className="ws-spark-line" data-alt="" vectorEffect="non-scaling-stroke" />}
      <path d={line} className="ws-spark-line" vectorEffect="non-scaling-stroke" />
    </svg>
  );
  if (!onAt)
    return (
      <div className="ws-spark-wrap" style={style} data-still="" role="img" aria-label={`${label}, the last two minutes`}>
        {svg}
      </div>
    );
  const pick = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    onAt(Math.round(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * last));
  };
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (e.key === 'Home' || e.key === 'End') onAt(e.key === 'Home' ? 0 : last);
    else if (step) onAt(Math.min(last, Math.max(0, (at ?? last) + step)));
    else if (e.key === 'Escape') onAt(null);
    else return;
    e.preventDefault();
  };
  const x = at === null ? 0 : (at / last) * 100;
  return (
    <div
      className="ws-spark-wrap"
      tabIndex={0}
      role="img"
      aria-label={`${label} chart; arrow keys read it`}
      onPointerMove={pick}
      onPointerDown={pick}
      onPointerLeave={() => onAt(null)}
      onKeyDown={keys}
      onBlur={() => onAt(null)}
    >
      {svg}
      {at !== null && (
        <>
          <span className="ws-spark-x" style={{ left: `${x}%` }} aria-hidden="true" />
          {alt && alt[at] !== undefined && <span className="ws-spark-dot" data-alt="" style={{ left: `${x}%`, top: yOf(alt[at], lo, hi, H) }} aria-hidden="true" />}
          <span className="ws-spark-dot" style={{ left: `${x}%`, top: yOf(vals[at], lo, hi, H) }} aria-hidden="true" />
        </>
      )}
    </div>
  );
}
